import { useContext, useEffect } from 'react';
import { Button } from '@orbita-ui/core';
import { Col, Row, Container } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { FormWithValidation } from 'shared/form';
import {
  ALL_ACCOUNTS_SELECT_OPTION,
  CuentaSelectorContext,
} from 'core/cuenta/contexts/CuentaSelectorContext';
import { ButtonPlaceholder, InputPlaceholder } from 'shared/placeholders';
import { SelectOption } from 'shared/form/models/SelectOption';
import { Cuenta } from 'core/cuenta/models/Cuenta';
import { EstadoTransferencia } from 'core/cuenta/models/Transferencia';
import { SearchTransfersRequest } from 'core/models/SearchTransfersRequest';
import { TransfersContext } from 'core/cuenta/contexts/TransfersContext';

const ESTADOS: {
  label: 'Todos' | EstadoTransferencia;
  value: 'Todos' | EstadoTransferencia;
}[] = [
  { label: 'Todos', value: 'Todos' },
  { label: 'Pendiente', value: 'Pendiente' },
  { label: 'Completada', value: 'Completada' },
  { label: 'Rechazada', value: 'Rechazada' },
];

const CLEAN_FORM_VALUE: Partial<FilterTransfersFormData> = {
  estado: ESTADOS.find((e) => e.value === 'Todos') as {
    label: EstadoTransferencia;
    value: EstadoTransferencia;
  },
  from: null,
  to: null,
  cuentaDestino: ALL_ACCOUNTS_SELECT_OPTION,
  cuentaOrigen: ALL_ACCOUNTS_SELECT_OPTION,
  username: '',
};

interface FilterTransfersFormData {
  cuentaOrigen: SelectOption<Cuenta>;
  cuentaDestino: SelectOption<Cuenta>;
  estado?: { label: 'Todos' | EstadoTransferencia; value: 'Todos' | EstadoTransferencia };
  from: Date | null;
  to: Date | null;
  username: string;
}

const mapFormData = (
  formData: Partial<FilterTransfersFormData>,
): SearchTransfersRequest => {
  return new SearchTransfersRequest({
    cuentaOrigen: formData?.cuentaOrigen,
    cuentaDestino: formData?.cuentaDestino,
    dateFrom: formData?.from,
    dateTo: formData?.to,
    estado: formData?.estado?.value,
    username: formData?.username,
  });
};

const mapRequestToForm = (
  request: SearchTransfersRequest,
  cuentas: SelectOption<Cuenta>[],
): FilterTransfersFormData => {
  const estadoStr = request.estado || 'Todos';
  return {
    estado: ESTADOS.find((e) => e.value === estadoStr) || ESTADOS[0],
    from: request.from || null,
    to: request.to || null,
    cuentaDestino:
      cuentas.find((c) => c.value?.numeroCuenta === request.cuentaDestino) ||
      ALL_ACCOUNTS_SELECT_OPTION,
    cuentaOrigen:
      cuentas.find((c) => c.value?.numeroCuenta === request.cuentaOrigen) ||
      ALL_ACCOUNTS_SELECT_OPTION,
    username: request.username || '',
  };
};

export const FilterTransfersForm = ({
  onSubmit,
}: {
  onSubmit: (request: SearchTransfersRequest) => void;
}) => {
  const { cuentaList, isLoading } = useContext(CuentaSelectorContext);
  const { searchParams } = useContext(TransfersContext);

  const methods = useForm<FilterTransfersFormData>({
    // values: formValues,
    defaultValues: {
      estado: null,
      from: null,
      to: null,
      cuentaDestino: null,
      cuentaOrigen: null,
      username: null,
    },
  });

  useEffect(() => {
    if (!isLoading && cuentaList.length > 0) {
      methods.reset(mapRequestToForm(searchParams, cuentaList));
    }
    // Solo queremos que esto pase cuando termina la carga inicial de cuentas
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, cuentaList.length]);

  const localSubmit = (data: FilterTransfersFormData) => {
    onSubmit(mapFormData(data));
  };

  return (
    <>
      {isLoading ? (
        <Container fluid className="d-flex flex-column gap-3 py-3">
          <Row>
            <Col>
              <InputPlaceholder />
            </Col>
            <Col>
              <InputPlaceholder />
            </Col>
            <Col>
              <InputPlaceholder />
            </Col>
          </Row>
          <Row>
            <Col>
              <InputPlaceholder />
            </Col>
            <Col>
              <InputPlaceholder />
            </Col>
            <Col>
              <InputPlaceholder />
            </Col>
          </Row>
          <div className="d-flex justify-content-end gap-2">
            <ButtonPlaceholder variant="outline" color="destructive">
              Limpiar
            </ButtonPlaceholder>
            <ButtonPlaceholder>Consultar</ButtonPlaceholder>
          </div>
        </Container>
      ) : (
        <FormWithValidation
          reactFormHookMethods={methods}
          onSubmit={localSubmit}
          className="m-3"
        >
          <Row>
            <Col>
              <FormWithValidation.Datepicker
                label="Desde"
                name="from"
                mode="single"
                fullWidthDialog
              />
            </Col>
            <Col>
              <FormWithValidation.Datepicker
                label="Hasta"
                name="to"
                mode="single"
                fullWidthDialog
              />
            </Col>
            <Col>
              <FormWithValidation.LegacySelect
                label="Estado"
                isSearchable={false}
                name="estado"
                options={ESTADOS}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FormWithValidation.LegacySelect
                label="Cuenta Origen"
                placeholder="Selecciona una cuenta"
                isSearchable
                name="cuentaOrigen"
                options={[ALL_ACCOUNTS_SELECT_OPTION, ...cuentaList]}
              />
            </Col>
            <Col>
              <FormWithValidation.LegacySelect
                label="Cuenta Destino"
                placeholder="Selecciona una cuenta"
                isSearchable
                name="cuentaDestino"
                options={[ALL_ACCOUNTS_SELECT_OPTION, ...cuentaList]}
                registerOptions={{
                  validate: (cuentaDestino: SelectOption<Cuenta>) => {
                    const cuentaOrigen: SelectOption<Cuenta> =
                      methods.getValues('cuentaOrigen');

                    if (!cuentaDestino?.value || !cuentaOrigen?.value) return true;

                    return (
                      String(cuentaDestino.value.numeroCuenta) !==
                        String(cuentaOrigen.value.numeroCuenta) ||
                      'La cuenta destino debe ser diferente a la cuenta origen'
                    );
                  },
                }}
              />
              <FormWithValidation.Control.Feedback
                type="invalid"
                controlId="cuentaDestino"
              />
            </Col>
            <Col>
              <FormWithValidation.TextInput
                label="Nombre de usuario"
                name="username"
                fullWidth
              />
            </Col>
          </Row>
          <div className="d-flex justify-content-end" style={{ gap: 8 }}>
            <Button
              type="button"
              variant="outline"
              color="destructive"
              onClick={() => methods.reset(CLEAN_FORM_VALUE)}
            >
              Limpiar
            </Button>
            <Button type="submit">Consultar</Button>
          </div>
        </FormWithValidation>
      )}
    </>
  );
};
